/**
 * OpportunityActionsMenu Component
 * 
 * Bottom sheet menu for opportunity actions:
 * - Link/Refresh parcel data
 * - Delete opportunity
 * 
 * Uses design tokens exclusively.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet, Modal, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, icon, touch } from '../../../shared/tokens/mobile';

interface OpportunityActionsMenuProps {
  visible: boolean;
  onClose: () => void;
  onEdit: () => void;
  onLinkParcel: () => void;
  onRefreshParcel: () => void;
  onDelete: () => void;
  linkingParcel?: boolean;
  hasParcelData?: boolean;
}

export function OpportunityActionsMenu({
  visible,
  onClose,
  onEdit,
  onLinkParcel,
  onRefreshParcel,
  onDelete,
  linkingParcel = false,
  hasParcelData = false,
}: OpportunityActionsMenuProps) {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.menuContainer} onPress={(e) => e.stopPropagation()}>
          <View style={styles.handle} />
          
          <Text style={styles.title}>Opportunity Actions</Text>
          
          {/* General Section */}
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>General</Text>
            <MenuItem
              iconName="create-outline"
              label="Edit Details"
              subtitle="Edit name, address, price, and other fields"
              onPress={onEdit}
            />
          </View>
          
          {/* Parcel Data Section */}
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>Parcel Data</Text>
            
            {!hasParcelData ? (
              <MenuItem
                iconName="layers-outline"
                label="Link Parcel Data"
                subtitle="Fetch acreage, tax value, owners from county records"
                onPress={onLinkParcel}
                loading={linkingParcel}
              />
            ) : (
              <MenuItem
                iconName="refresh-outline"
                label="Refresh Parcel Data"
                subtitle="Re-fetch latest data from county records"
                onPress={onRefreshParcel}
                loading={linkingParcel}
              />
            )}
          </View>
          
          {/* Danger Zone */}
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>Danger Zone</Text>
            <MenuItem
              iconName="trash-outline"
              label="Delete Opportunity"
              subtitle="Permanently remove this opportunity"
              onPress={onDelete}
              danger
            />
          </View>
          
          {/* Cancel */}
          <Pressable onPress={onClose} style={styles.cancelButton}>
            <Text style={styles.cancelText}>Cancel</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

interface MenuItemProps {
  iconName: keyof typeof Ionicons.glyphMap;
  label: string;
  subtitle?: string;
  onPress: () => void;
  loading?: boolean;
  danger?: boolean;
}

function MenuItem({ iconName, label, subtitle, onPress, loading, danger }: MenuItemProps) {
  const iconColor = danger ? color.error : color.textPrimary;
  const labelColor = danger ? color.error : color.textPrimary;
  
  return (
    <Pressable
      onPress={onPress}
      disabled={loading}
      style={({ pressed }) => [
        styles.menuItem,
        pressed && styles.menuItemPressed,
      ]}
    >
      <View style={styles.menuItemIcon}>
        {loading ? (
          <ActivityIndicator size="small" color={color.primary} />
        ) : (
          <Ionicons name={iconName} size={icon.md} color={iconColor} />
        )}
      </View>
      <View style={styles.menuItemContent}>
        <Text style={[styles.menuItemLabel, { color: labelColor }]}>{label}</Text>
        {subtitle && <Text style={styles.menuItemSubtitle}>{subtitle}</Text>}
      </View>
      <Ionicons name="chevron-forward" size={icon.sm} color={color.textMuted} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  menuContainer: {
    backgroundColor: color.surface,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    paddingTop: space.sm,
    paddingBottom: space.xxl,
    paddingHorizontal: space.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  handle: {
    width: 36,
    height: 4,
    backgroundColor: color.border,
    borderRadius: radius.full,
    alignSelf: 'center',
    marginBottom: space.md,
  },
  title: {
    fontSize: text.sectionTitle.fontSize,
    fontWeight: text.sectionTitle.fontWeight,
    color: color.textPrimary,
    textAlign: 'center',
    marginBottom: space.lg,
  },
  section: {
    marginBottom: space.md,
  },
  sectionLabel: {
    fontSize: text.caption.fontSize,
    fontWeight: '600',
    color: color.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: space.sm,
    paddingHorizontal: space.xs,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: space.md,
    paddingHorizontal: space.sm,
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.md,
    marginBottom: space.xs,
    minHeight: touch.recommended,
  },
  menuItemPressed: {
    backgroundColor: color.border,
  },
  menuItemIcon: {
    width: icon.lg,
    alignItems: 'center',
    marginRight: space.sm,
  },
  menuItemContent: {
    flex: 1,
  },
  menuItemLabel: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
  },
  menuItemSubtitle: {
    fontSize: text.caption.fontSize,
    color: color.textSecondary,
    marginTop: 2,
  },
  cancelButton: {
    paddingVertical: space.md,
    alignItems: 'center',
    marginTop: space.sm,
  },
  cancelText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.primary,
  },
});

